import React, { Suspense, useEffect, lazy, useMemo, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TranslationController as i18nController } from '@freshworks/crayons/react';
import { withAnalytics } from 'react-analytics-collector';
import { useMessageChannelAdapter } from 'hooks';
import { PageLoader } from 'UI';
import { useFreshdeskContextState, useFreshdeskContextUpdater } from 'store';
import Entities from 'routes/entities';
import Redirect from 'routes/redirect';
import { PageNotFound } from 'routes/error';
import { LEGO_CONSTANTS } from 'constants/index';
import { HOST_DEFAULT_FEATURE_FLAG_CONFIGS } from 'configs/featureFlagConfigs';
import { mergeHostConfigs } from 'utils/configUtils';
export { RouteWithAnalytics };

const EntityRoute = lazy(() => import('routes/entity'));
const RecordRoute = lazy(() => import('routes/records'));
const Create = lazy(() => import('routes/create'));

const RedirectToSchemas = () => <Navigate to={`${LEGO_CONSTANTS.ROUTE_PREFIX}/schemas`} replace />;

/**
 * RouteWithAnalytics wires react-analytics-collector into each route while keeping React Router v6
 * rendering correct. withAnalytics(name, Component, routeProps) must be called during render:
 * the library uses hooks internally, so calling it from useEffect causes "Invalid hook call".
 * We render the page Component ourselves and do not use the library's return value for rendering
 * (it is built for React Router v5 and would break with v6). history.push/replace accept (to, state)
 * and forward state to navigate for v5-style API compatibility.
 *
 * @see https://github.com/freshworksinc/react-analytics-collector/releases/tag/3.0.0-beta.2
 */
function RouteWithAnalytics({ name, Component }) {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  const routeProps = useMemo(
    () => ({
      location,
      match: { params },
      history: {
        push: (to, state) => navigate(to, { state }),
        replace: (to, state) => navigate(to, { replace: true, state }),
      },
    }),
    [location, params, navigate]
  );

  withAnalytics(name, Component, routeProps);
  return React.createElement(Component, null);
}

const AppBody = () => {
  const { getAccountBasePath } = useFreshdeskContextState();
  return (
    <Suspense fallback={<></>}>
      <BrowserRouter basename={getAccountBasePath()}>
        <Routes>
          <Route path="/" element={<RouteWithAnalytics name="entities" Component={RedirectToSchemas} />} />
          <Route path={`${LEGO_CONSTANTS.ROUTE_PREFIX}`} element={<RouteWithAnalytics name="entities" Component={RedirectToSchemas} />} />
          <Route path={`${LEGO_CONSTANTS.ROUTE_PREFIX}/schemas/:id/records`} element={<RouteWithAnalytics name="entity.records" Component={EntityRoute} />} />
          <Route path={`${LEGO_CONSTANTS.ROUTE_PREFIX}/schemas/:id/records/:displayId`} element={<RouteWithAnalytics name="entity.records.record" Component={RecordRoute} />} />
          <Route path={`${LEGO_CONSTANTS.ROUTE_PREFIX}/schemas`} element={<RouteWithAnalytics name="schema" Component={Entities} />} />
          <Route path={`${LEGO_CONSTANTS.ROUTE_PREFIX}/schemas/create`} element={<RouteWithAnalytics name="schema.create" Component={Create} />} />
          <Route path={`${LEGO_CONSTANTS.ROUTE_PREFIX}/schemas/edit/:id`} element={<RouteWithAnalytics name="schema.edit" Component={Create} />} />
          <Route path="/redirect" element={<RouteWithAnalytics name="redirect" Component={Redirect} />} />
          <Route path="*" element={<RouteWithAnalytics name="pageNotFound" Component={PageNotFound} />} />
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
};

function App() {
  const { getAccessToParentAppContext } = useMessageChannelAdapter();
  const { i18n } = useTranslation();
  const updateFreshdeshContext = useFreshdeskContextUpdater();
  const { getUserDetails } = useFreshdeskContextState();
  const appLoadingTimeout = useRef(null);
  const [loadApp, setLoadApp] = useState(false);
  const language = getUserDetails()?.locale?.language;
  useEffect(() => {
    (async () => {
      i18nController?.setLang?.(language || 'en-US');
      // platformUII18nController?.setLang?.(language || 'en-US');
      document.documentElement.setAttribute('lang', language || 'en-US');
      await i18n.changeLanguage(language?.includes('-') ? language : i18nController?.langCodeMapping[language] || 'en-US');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    (async () => {
      const context = { resource: ['account', 'organisation', 'permissions', 'user', 'native_objects_enabled', 'collections'] };
      const response = await getAccessToParentAppContext(context);
      const defaultConfigs = HOST_DEFAULT_FEATURE_FLAG_CONFIGS.configs;
      if (response.success) {
        const { data, configs: hostConfigs } = response.response;
        window.basepath = data.basepath;
        window.productSuffix = data.productSuffix;
        const mergedConfigs = mergeHostConfigs(defaultConfigs, hostConfigs ?? {});
        updateFreshdeshContext({
          ...data,
          configs: mergedConfigs,
          loaded: true,
        });
      }
    })();
  }, [getAccessToParentAppContext, updateFreshdeshContext]);

  const userId = getUserDetails()?.id;
  useEffect(() => {
    if (userId) {
      setLoadApp(true);
      clearTimeout(appLoadingTimeout.current);
    } else if (!appLoadingTimeout.current) {
      appLoadingTimeout.current = setTimeout(() => {
        setLoadApp(true);
      }, 3000);
    }
  }, [userId]);

  return useMemo(() => {
    return loadApp ? <AppBody /> : <PageLoader />;
  }, [loadApp]);
}

export default App;
